/**
 * The player's vehicle, parked where they live.
 *
 * ADR-0008 decided against a drivable vehicle for this phase. What is cheap and
 * genuinely valuable is making ownership *visible*: the research is explicit
 * that money must convert into something the player can look at, and until now
 * buying a pickup changed a travel-time multiplier and nothing else.
 *
 * So the vehicle you own is parked on your driveway. You walk past it every
 * morning. It is the clearest possible read on how far you have come.
 */
import * as THREE from 'three';
import { standard } from './Materials.js';
import { contactShadow } from './Neighbourhood.js';

export interface VehicleRig {
  group: THREE.Group;
  /** Show whichever vehicle the player currently owns, or none. */
  setOwned(id: string | null): void;
  /** Whether what is shown can actually be driven (ADR-0011: cars only). */
  drivable(id: string | null): boolean;
  /** Spin the wheels in proportion to speed, and light the brakes. */
  animate(speed: number, braking: boolean, dt: number): void;
}

export function createVehicles(x: number, z: number, facing: number): VehicleRig {
  const group = new THREE.Group();
  group.position.set(x, 0, z);
  group.rotation.y = facing;

  const bicycle = buildBicycle();
  bicycle.visible = false;
  group.add(bicycle);

  const hatchback = buildHatchback();
  hatchback.visible = false;
  group.add(hatchback);

  const pickup = buildPickup();
  pickup.visible = false;
  group.add(pickup);

  // Collected once rather than searched per frame: the wheel spin runs every
  // frame the car moves, and traversing the tree for it would be the kind of
  // small waste that only shows up once traffic is doing it too.
  const wheels: THREE.Object3D[] = [];
  for (const root of [hatchback, pickup]) {
    root.traverse((o) => { if (o.userData.wheel) wheels.push(o); });
  }
  const brakeLights: THREE.MeshStandardMaterial[] = [];
  for (const root of [hatchback, pickup]) {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh && o.userData.brake) {
        brakeLights.push(mesh.material as THREE.MeshStandardMaterial);
      }
    });
  }

  return {
    group,
    setOwned(id: string | null): void {
      bicycle.visible = id === 'bicycle';
      hatchback.visible = id === 'hatchback_old';
      pickup.visible = id === 'pickup_old';
    },
    drivable(id: string | null): boolean {
      // A bicycle is ridden, not driven. Running it through car dynamics
      // would give it a reverse gear and a turning circle, which is worse
      // than leaving it as the travel-time upgrade it already is.
      return id === 'hatchback_old' || id === 'pickup_old';
    },
    animate(speed: number, braking: boolean, dt: number): void {
      if (Math.abs(speed) > 0.01) {
        // Rolling radius is about 0.34 m; close enough that the wheels do not
        // visibly skate at the speeds this car reaches.
        const spin = (speed / 0.34) * dt;
        for (const w of wheels) w.rotation.x -= spin;
      }
      const lit = braking ? 1.6 : 0;
      for (const m of brakeLights) {
        m.emissiveIntensity += (lit - m.emissiveIntensity) * Math.min(1, dt * 12);
      }
    },
  };
}

/**
 * A small, tired hatchback: the first thing you own with a roof.
 *
 * Deliberately unglamorous. The progression fantasy needs a bottom rung that
 * reads as "this is what I could afford", so it is narrow, dented in profile
 * and a colour nobody chose.
 */
function buildHatchback(): THREE.Group {
  const car = new THREE.Group();
  const bodyMat = standard(0x9a9384, { rough: 0.62, metal: 0.2 });
  const trimMat = standard(0x33363c, { rough: 0.75 });
  const glassMat = standard(0x222f38, { rough: 0.09, metal: 0.5 });
  const tyreMat = standard(0x1a1c20, { rough: 0.95 });

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.82, 0.66, 4.1), bodyMat);
  body.position.y = 0.78;
  body.castShadow = true;
  car.add(body);

  // The cabin is a separate, narrower box set back from the nose: that offset
  // is most of what distinguishes a car silhouette from a brick.
  const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.72, 0.7, 2.15), bodyMat);
  cabin.position.set(0, 1.4, -0.16);
  cabin.castShadow = true;
  car.add(cabin);

  const windscreen = new THREE.Mesh(new THREE.BoxGeometry(1.58, 0.5, 0.07), glassMat);
  windscreen.position.set(0, 1.44, 0.9);
  windscreen.rotation.x = -0.28;
  car.add(windscreen);
  const rear = new THREE.Mesh(new THREE.BoxGeometry(1.58, 0.46, 0.07), glassMat);
  rear.position.set(0, 1.44, -1.22);
  rear.rotation.x = 0.34;
  car.add(rear);
  for (const sx of [-1, 1]) {
    const side = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.42, 1.8), glassMat);
    side.position.set(sx * 0.85, 1.46, -0.16);
    car.add(side);
  }

  for (const pz of [2.03, -2.03]) {
    const bumper = new THREE.Mesh(new THREE.BoxGeometry(1.86, 0.24, 0.14), trimMat);
    bumper.position.set(0, 0.66, pz);
    car.add(bumper);
  }

  for (const sx of [-0.58, 0.58]) {
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.18, 0.09), standard(0xf2ead2));
    head.position.set(sx, 0.94, 2.03);
    car.add(head);

    const tail = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.16, 0.08),
      standard(0x8a1f1a, { rough: 0.4 }),
    );
    (tail.material as THREE.MeshStandardMaterial).emissive = new THREE.Color(0xff3b2e);
    (tail.material as THREE.MeshStandardMaterial).emissiveIntensity = 0;
    tail.position.set(sx, 0.96, -2.03);
    tail.userData.brake = true;
    car.add(tail);
  }

  for (const sx of [-0.93, 0.93]) {
    for (const pz of [1.28, -1.28]) {
      const wheel = new THREE.Group();
      wheel.position.set(sx, 0.34, pz);
      const tyre = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.24, 12), tyreMat);
      tyre.rotation.z = Math.PI / 2;
      tyre.castShadow = true;
      wheel.add(tyre);
      const hub = new THREE.Mesh(
        new THREE.CylinderGeometry(0.15, 0.15, 0.26, 8),
        standard(0x8f959d, { rough: 0.45, metal: 0.6 }),
      );
      hub.rotation.z = Math.PI / 2;
      wheel.add(hub);
      wheel.userData.wheel = true;
      car.add(wheel);
    }
  }

  car.add(contactShadow(2.4, 4.6));
  return car;
}

function buildBicycle(): THREE.Group {
  const bike = new THREE.Group();
  const frameMat = standard(0x2f7fa8, { rough: 0.4, metal: 0.55 });
  const tyreMat = standard(0x1a1c20, { rough: 0.9 });

  for (const z of [-0.62, 0.62]) {
    const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.05, 6, 16), tyreMat);
    wheel.position.set(0, 0.34, z);
    wheel.castShadow = true;
    bike.add(wheel);
  }
  const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 1.2, 6), frameMat);
  bar.rotation.x = Math.PI / 2;
  bar.position.set(0, 0.58, 0);
  bike.add(bar);
  const seatPost = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.4, 6), frameMat);
  seatPost.position.set(0, 0.72, -0.32);
  bike.add(seatPost);
  const seat = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 0.26), standard(0x22242a));
  seat.position.set(0, 0.93, -0.34);
  bike.add(seat);
  const bars = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.46, 6), frameMat);
  bars.rotation.z = Math.PI / 2;
  bars.position.set(0, 0.94, 0.44);
  bike.add(bars);

  bike.add(contactShadow(1.4, 2.0));
  return bike;
}

function buildPickup(): THREE.Group {
  const truck = new THREE.Group();
  const bodyMat = standard(0x3f6b8a, { rough: 0.45, metal: 0.25 });
  const trimMat = standard(0x2a2d33, { rough: 0.7 });
  const glassMat = standard(0x1e2c38, { rough: 0.1, metal: 0.5 });
  const tyreMat = standard(0x1a1c20, { rough: 0.95 });

  const chassis = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.62, 5.0), bodyMat);
  chassis.position.y = 0.86;
  chassis.castShadow = true;
  truck.add(chassis);

  const cab = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.82, 1.7), bodyMat);
  cab.position.set(0, 1.55, 0.7);
  cab.castShadow = true;
  truck.add(cab);

  const windscreen = new THREE.Mesh(new THREE.BoxGeometry(1.74, 0.6, 0.08), glassMat);
  windscreen.position.set(0, 1.6, -0.16);
  truck.add(windscreen);
  for (const sx of [-1, 1]) {
    const side = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.52, 1.4), glassMat);
    side.position.set(sx * 0.94, 1.6, 0.72);
    truck.add(side);
  }

  // Open load bed with side walls - this is the thing that says "work truck".
  for (const [px, pz, w, d] of [
    [0, -2.34, 1.98, 0.14], [-0.95, -1.2, 0.12, 2.4], [0.95, -1.2, 0.12, 2.4],
  ] as [number, number, number, number][]) {
    const wall = new THREE.Mesh(new THREE.BoxGeometry(w, 0.5, d), bodyMat);
    wall.position.set(px, 1.4, pz);
    wall.castShadow = true;
    truck.add(wall);
  }

  const bumperFront = new THREE.Mesh(new THREE.BoxGeometry(2.06, 0.2, 0.16), trimMat);
  bumperFront.position.set(0, 0.72, 2.5);
  truck.add(bumperFront);

  for (const sx of [-0.62, 0.62]) {
    const light = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.2, 0.1), standard(0xf0e6c8));
    light.position.set(sx, 1.05, 2.48);
    truck.add(light);

    const tail = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 0.18, 0.09),
      standard(0x8a1f1a, { rough: 0.4 }),
    );
    (tail.material as THREE.MeshStandardMaterial).emissive = new THREE.Color(0xff3b2e);
    (tail.material as THREE.MeshStandardMaterial).emissiveIntensity = 0;
    tail.position.set(sx, 1.1, -2.42);
    tail.userData.brake = true;
    truck.add(tail);
  }

  // Wheels hang off a group so the spin is a rotation about the group's own X
  // axis. Spinning the cylinder directly would fight the Z rotation that
  // stands it up, and the wheel would wobble instead of turn.
  for (const sx of [-1.02, 1.02]) {
    for (const pz of [1.5, -1.5]) {
      const wheel = new THREE.Group();
      wheel.position.set(sx, 0.46, pz);
      const tyre = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.46, 0.3, 12), tyreMat);
      tyre.rotation.z = Math.PI / 2;
      tyre.castShadow = true;
      wheel.add(tyre);
      const hub = new THREE.Mesh(
        new THREE.CylinderGeometry(0.2, 0.2, 0.32, 10),
        standard(0x9aa0a8, { rough: 0.35, metal: 0.7 }),
      );
      hub.rotation.z = Math.PI / 2;
      wheel.add(hub);
      wheel.userData.wheel = true;
      truck.add(wheel);
    }
  }

  truck.add(contactShadow(3.0, 5.6));
  return truck;
}
